/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { act, useEffect, useMemo, useRef, useState } from "react";
import {
  Brain,
  CheckCircle,
  Loader2,
  Bot,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

import { useParams, useSearchParams } from "react-router-dom";

import moment from "moment-timezone";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../common/table";
import {
  agentLabels,
  agentResIds,
  getAgentIconKey,
  getFileIcon,
} from "../../common/Constants";
import { useJobDescriptionFormStore } from "./useJobDescriptionFormStore";
import { CardContent, CardTitle } from "../../common/Card";
import { Textarea } from "../../common/textarea";
import { Label } from "../../common/label";
import { Input } from "../../common/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../common/select";
import { useParsedStoredPrompt } from "../hooks/useParsedStoredPrompt";
import useCommonStore from "../../store/CommonStore";
import { useInterviewFormStore } from "./useInterviewFormStore";
import { ChatDetails, useChatDetails } from "../../api/api-hooks";
import LlmResponseFormat from "./LlmResponseFormat";

interface AgentChatViewProps {
  events?: any[];
  isActive?: boolean;
}

const AgentChatView: React.FC<AgentChatViewProps> = ({
  // events,
  isActive,
}) => {
  //   const { toast } = useToast();
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const formRef = useRef<HTMLDivElement | null>(null);
  const finalResultRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const agentParam = searchParams.get("agent");
  const agentName = agentParam ? decodeURIComponent(agentParam) : "";

  const hasScrolledOnLoad = useRef(false);

  const lastPopulatedPromptId = useRef<string | null>(null);
  const analyzingTopRef = useRef<HTMLDivElement | null>(null);

  const { agentId } = useParams();

  const { data: chatData }: { data: ChatDetails | undefined } = useChatDetails(
    agentId as string
  );

  const iconKey = getAgentIconKey(chatData?.agent || agentName);

  // const location = useLocation();
  // const analysisData = location.state?.analysisData;

  const { isAnalyzing, activeResTab, setActiveResTab } = useCommonStore();

  const [showAll, setShowAll] = useState(false);

  const [showAnalysis, setShowAnalysis] = useState(true);

  const [isEditingForm, setIsEditingForm] = useState(false);

  const jobDescriptionStore = useJobDescriptionFormStore();
  const interviewStore = useInterviewFormStore();

  // reverse: label -> iconKey (one-liner)
  const labelToIconKey: Record<string, string> = Object.fromEntries(
    Object.entries(agentLabels).map(([k, v]) => [v, k])
  );

  const raw = chatData?.agent || agentName || "";

  const iconInputKey = labelToIconKey[raw] ?? raw;

  const isJobDescription = iconInputKey === "JD_Creation_Crew";
  const isInterview = iconInputKey === "Job_Interview_Crew";
  const isCountry = iconInputKey === "Country_Onboarding_Crew";
  const isResume = iconInputKey === "Resume_Builder_Crew";
  const isChro = iconInputKey === "CHRO_Comprehensive_Crew";

  const agentCondition =
    isJobDescription || isInterview || isCountry || isResume || isChro;

  const { formData, updateField } = isInterview
    ? interviewStore
    : jobDescriptionStore;

  // const { formData, updateField, getInputPrompt } = useJobDescriptionFormStore();

  const agentChatName = chatData?.agent;

  const [expandedAgents, setExpandedAgents] = useState<Record<number, boolean>>(
    {}
  );

  const toggleAgentVisibility = (index: number) => {
    setExpandedAgents((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const storedPromptJson =
    chatData?.prompts?.find((p: any) => p?.prompt_message_id)?.prompt || "";

  const parsedStoredPrompt = useParsedStoredPrompt(storedPromptJson);

  const storedPromptFiles =
    chatData?.prompts?.find((p: any) => p?.prompt_message_id)?.filename || "";

  //   const storedPromptDatabase =
  //     chatData?.prompts?.find((p: any) => p?.prompt_message_id)?.database || "";

  const meta = parsedStoredPrompt || {};

  // const parsedStoredPrompt = (() => {
  //   try {
  //     return typeof storedPromptJson === "string"
  //       ? JSON.parse(storedPromptJson)
  //       : {};
  //   } catch {
  //     return {};
  //   }
  // })();

  const storedUseDetailedForm = parsedStoredPrompt?.useDetailedForm;

  const storedFiles = Array.isArray(storedPromptFiles)
    ? storedPromptFiles
    : storedPromptFiles
    ? [storedPromptFiles]
    : [];

  const files = storedFiles || [];

  const shouldShowButton = files.length > 3;

  const useDetailedForm = formData.useDetailedForm || storedUseDetailedForm;

  const targetPromptId = chatData?.messages?.[0]?.prompt_message_id;

  const matchedCrewEvents = (chatData?.messages?.[0]?.crew_event || []).filter(
    (event: any) => event.prompt_message_id === targetPromptId
  );

  const combinedEvents = [...matchedCrewEvents];

  const combinedFiles = [...(storedFiles || []), ...(files || [])];

  // Enhanced agent results extraction with more event types and better data parsing
  const agentResults = combinedEvents
    .filter((event) => {
      if (event.type === "task_complete") return false;
      return (
        event.type === "agent_finish" ||
        event.type === "crew_complete" ||
        event.type === "analysis_complete" ||
        (event.type === "message" && event.data?.content) ||
        (event.data &&
          typeof event.data === "object" &&
          (event.data.result || event.data.content || event.data.message)) ||
        event.result ||
        event.final_result
      );
    })
    .map((event) => {
      const agentName =
        event.agent_name ||
        event.data?.agent_name ||
        event.data?.agent ||
        (event.type === "crew_complete" ? "Generated Result" : "");

      let result =
        event.result ||
        event.final_result ||
        event.data?.result ||
        event.data?.content ||
        event.data?.message ||
        event.data?.final_result;

      if (typeof event.data === "string" && !result) {
        try {
          const parsed = JSON.parse(event.data);
          result =
            parsed.result ||
            parsed.content ||
            parsed.message ||
            parsed.final_result;
        } catch (e) {
          result = event.data;
        }
      }

      if (!result) {
        result = "Processing...";
      }

      // Filter out content that contains standalone "AI Agent" references
      if (typeof result === "string") {
        // Remove sections that start with "AI Agent" or contain standalone "AI Agent" mentions
        result = result
          .split("\n")
          .filter((line) => {
            const trimmedLine = line.trim().toLowerCase();
            // Filter out lines that are just "ai agent" or start with "ai agent:"
            return !(
              trimmedLine === "ai agent" ||
              trimmedLine.startsWith("ai agent:") ||
              trimmedLine.startsWith("**ai agent**") ||
              (trimmedLine.includes("ai agent") &&
                trimmedLine.length < 50 &&
                !trimmedLine.includes("analysis"))
            );
          })
          .join("\n")
          .trim();
      }

      const timestamp =
        event.data?.timestamp || event.timestamp || new Date().toISOString();

      // const taskDescription =
      //   event.task_description || event.data?.task_description || "";

      return {
        agentName,
        result,
        timestamp,
        // taskDescription,
        type: event.type,
      };
    })
    .filter(
      (result) =>
        result.result &&
        result.result !== "Processing..." &&
        result.result.trim().length > 0
    );

  const hasGeneratedResult = useMemo(
    () => agentResults.some((r) => r.agentName === "Generated Result"),
    [agentResults]
  );

  const hasResults = agentResults.length > 0;

  const [analysisStatus, setAnalysisStatus] = useState("Idle");
  const [progressComplete, setProgressComplete] = useState(false);

  const generatedResultExists = agentResults.find(
    (agentResult) => agentResult.agentName === "Generated Result"
  );

  //   const { startAnalysis, resetAnalysis } = useCrewAnalysis({
  //     agentType: agentKey,
  //     // meta: selectedSubmission,
  //     successMessage: "Validation completed successfully",
  //     errorMessage: "Validation failed",
  //     meta: {
  //       // Job description store has all fields
  //       jobTitle: formData.jobTitle,
  //       department: formData.department,
  //       customDepartment: formData.customDepartment,
  //       companyName: formData.companyName,
  //       employmentType: formData.employmentType,
  //       experienceLevel: formData.experienceLevel,
  //       location: formData.location,
  //       keySkills: formData.keySkills,
  //       salaryRange: formData.salaryRange,
  //       benefits: formData.benefits,
  //       useDetailedForm: formData.useDetailedForm,
  //       prompt: formData.prompt,
  //     },
  //   });

  const maxExpectedAgents = 8;
  const stepSize = 85 / maxExpectedAgents;

  // Effect to handle animation logic
  useEffect(() => {
    if (hasResults && !generatedResultExists) {
      setAnalysisStatus("In Progress");
      setProgressComplete(false);
    } else if (hasResults && generatedResultExists) {
      setAnalysisStatus("Complete");
      setProgressComplete(true);
    } else if (!hasResults) {
      setAnalysisStatus("Idle");
      setProgressComplete(false);
    }
  }, [hasResults, generatedResultExists]);

  useEffect(() => {
    if (agentResults.length === 0) return;

    setExpandedAgents((prev) => {
      const updated = { ...prev };
      agentResults.forEach((_, index) => {
        if (!(index in updated)) {
          updated[index] = true;
        }
      });
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentResults.length]);

  useEffect(() => {
    if (agentResults.length === 0) return;

    const scrollToElement = (element: HTMLElement | null, delay = 150) => {
      if (!element) return;
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, delay);
    };

    if (!hasScrolledOnLoad.current) {
      hasScrolledOnLoad.current = true;

      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 0);
      return;
    }

    const generatedIndex = agentResults.findIndex(
      (r) => r.agentName === "Generated Result"
    );

    const hasGenerated = generatedIndex !== -1;

    if (hasGenerated && finalResultRef.current) {
      scrollToElement(finalResultRef.current);
    } else {
      const lastNonGeneratedIndex = [...agentResults]
        .map((r, i) => ({ name: r.agentName, i }))
        .reverse()
        .find((r) => r.name !== "Generated Result")?.i;

      const lastEl =
        lastNonGeneratedIndex !== undefined
          ? itemRefs.current[lastNonGeneratedIndex]
          : null;

      scrollToElement(lastEl, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentResults.length]);

  useEffect(() => {
    if (!parsedStoredPrompt || typeof parsedStoredPrompt !== "object") return;

    const promptId = JSON.stringify(parsedStoredPrompt);

    if (lastPopulatedPromptId.current === promptId) return;

    const fields = [
      "jobTitle",
      "department",
      "customDepartment",
      "companyName",
      "employmentType",
      "experienceLevel",
      "location",
      "keySkills",
      "jobResponsibilities",
      "jobRequirements",
      "salaryRange",
      "benefits",
      "prompt",
    ];

    const hasValidData = fields.some((key) => {
      const value = parsedStoredPrompt[key];
      return (
        value !== undefined && value !== null && String(value).trim() !== ""
      );
    });

    if (!hasValidData) return;

    setTimeout(() => {
      fields.forEach((field) => {
        const value = parsedStoredPrompt[field];
        if (value !== undefined && value !== null) {
          updateField(field as any, String(value).trim());
        }
      });

      lastPopulatedPromptId.current = promptId;
    }, 0);
  }, [parsedStoredPrompt, updateField]);

  const getProgressWidth = () => {
    if (progressComplete) return 100;
    if (!hasResults) return 0;
    const currentStepValue = hasResults ? agentResults.length : 0;
    return Math.min(currentStepValue * stepSize, 85);
  };

  const isComplete = combinedEvents.some(
    (e: any) =>
      e.type === "crew_complete" ||
      e.type === "analysis_complete" ||
      e.type === "task_complete" ||
      (e.final_result && e.final_result.trim().length > 0)
  );

  const hasError = combinedEvents.some((e: any) => e.type === "error");

  if (
    !isAnalyzing &&
    !hasResults &&
    combinedEvents.length === 0 &&
    (!chatData ||
      typeof chatData !== "object" ||
      Object.keys(chatData).length === 0)
  ) {
    return null;
  }

  // // Enhanced ReactMarkdown components with improved table support and p tag handling
  // const markdownComponents = {
  //   // Handle paragraphs that might contain table markup
  //   p: ({ children }: any) => {
  //     // Check if the paragraph contains table-like content
  //     const content = children?.toString() || "";
  //     const hasTableContent = content.includes("|") && content.includes("---");

  //     if (hasTableContent) {
  //       // Parse table content from paragraph
  //       const lines = content.split("\n").filter((line: any) => line.trim());
  //       if (lines.length >= 2) {
  //         const headerLine = lines[0];
  //         const separatorLine = lines[1];
  //         const dataLines = lines.slice(2);

  //         // Check if it looks like a table
  //         if (separatorLine.includes("---") || separatorLine.includes("|")) {
  //           const headers = headerLine
  //             .split("|")
  //             .map((h: any) => h.trim())
  //             .filter((h: any) => h);
  //           const rows = dataLines.map((line: any) =>
  //             line
  //               .split("|")
  //               .map((cell: any) => cell.trim())
  //               .filter((cell: any) => cell)
  //           );

  //           return (
  //             <div className="my-4 overflow-auto">
  //               <Table className="w-full">
  //                 <TableHeader>
  //                   <TableRow>
  //                     {headers.map((header: any, index: any) => (
  //                       <TableHead key={index} className="font-semibold">
  //                         {header}
  //                       </TableHead>
  //                     ))}
  //                   </TableRow>
  //                 </TableHeader>
  //                 <TableBody>
  //                   {rows.map((row: any, rowIndex: any) => (
  //                     <TableRow key={rowIndex}>
  //                       {row.map((cell: any, cellIndex: any) => (
  //                         <TableCell key={cellIndex}>{cell}</TableCell>
  //                       ))}
  //                     </TableRow>
  //                   ))}
  //                 </TableBody>
  //               </Table>
  //             </div>
  //           );
  //         }
  //       }
  //     }

  //     // Regular paragraph
  //     return <p className="my-2">{children}</p>;
  //   },
  //   table: ({ children }: any) => (
  //     <div className="my-4 overflow-auto">
  //       <Table className="w-full">{children}</Table>
  //     </div>
  //   ),
  //   thead: ({ children }: any) => <TableHeader>{children}</TableHeader>,
  //   tbody: ({ children }: any) => <TableBody>{children}</TableBody>,
  //   tr: ({ children }: any) => <TableRow>{children}</TableRow>,
  //   th: ({ children }: any) => (
  //     <TableHead className="font-semibold">{children}</TableHead>
  //   ),
  //   td: ({ children }: any) => <TableCell>{children}</TableCell>,
  //   code: ({ inline, children }: any) => {
  //     if (inline) {
  //       return (
  //         <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
  //           {children}
  //         </code>
  //       );
  //     }
  //     return (
  //       <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto break-after-all break-before-all break-all max-w-[350px] sm:max-w-screen-lg w-full">
  //         <code>{children}</code>
  //       </pre>
  //     );
  //   },
  // };

  const getItemsToShow = () => {
    if (showAll) return combinedFiles;
    return combinedFiles.slice(0, 3);
  };

  return (
    <>
      <div className="max-w-[968px] w-full mx-auto px-4 py-6 pt-20 xl:pt-8">
        <div className="bg-white rounded-lg border border-custom-border-default p-4  sticky top-0 z-50">
          <div
            className={`flex items-center gap-3 mb-4 ${
              !hasResults && isAnalyzing ? "animate-pulse" : ""
            }`}
          >
            <div
              className={`w-10 h-10 ${iconKey} rounded flex items-center justify-center`}
            >
              <img
                src={`/icons/response/${iconKey}.svg`}
                alt=""
                className="w-6"
              />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-primary">
                <h1>{agentLabels[iconKey] || ""}</h1>
              </h1>
              <p className="text-sm text-gray-500">Analysis Report</p>
            </div>
          </div>

          {/* Status Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm text-gray-600 ${
                  !hasResults && isAnalyzing ? "animate-pulse" : ""
                }`}
              >
                Analysis Status
              </span>
            </div>
            {analysisStatus === "Complete" && (
              <div className="flex items-center gap-4">
                <span
                  className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${
                    analysisStatus === "Complete"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {analysisStatus}
                </span>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-custom-background rounded-full h-2 mb-6">
            <div
              className={`h-full bg-primary_1 rounded-full transition-all duration-1000 ease-out ${
                agentResults.length > 0 && !progressComplete
                  ? "animate-pulse"
                  : ""
              }`}
              style={{
                width: `${getProgressWidth()}%`,
                animationDuration: "1.5s",
                animationIterationCount:
                  agentResults.length > 0 && !progressComplete ? "1" : "0",
              }}
            ></div>
          </div>
        </div>

        {/* Input Section - Always Visible */}
        {chatData?.agent !== "Country_Onboarding_Specialist" && (
          <div className="bg-white rounded-lg border border-custom-border-default mt-6">
            <div
              className={`flex items-center justify-between p-4 pb-0 ${
                !hasResults && isAnalyzing ? "animate-pulse" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-primary_1/10 rounded-lg flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary_1" />
                </div>
                <h2 className="text-lg font-medium text-primary_1">
                  {agentLabels[iconKey] || agentChatName} Input
                </h2>
              </div>
              {/* {isEditingForm && (
                <button
                  onClick={() => {
                    setIsEditingForm(false);
                  }}
                >
                  "Editing Form OFF"
                </button>
              )}
              {isEditingPrompt && (
                <button
                  onClick={() => {
                    setIsEditingPrompt(false);
                  }}
                >
                  "Editing Prompt OFF"
                </button>
              )} */}
            </div>

            <div className="p-4">
              {agentCondition && (
                <div ref={formRef}>
                  {!isEditingForm ? (
                    // Display mode for Job Description Creation and Interview
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {meta?.companyName && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Company Name *
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.companyName}
                            </div>
                          </div>
                        )}

                        {meta?.jobTitle && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Job Title *
                            </label>
                            <div
                              className={`p-3 py-2  h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.jobTitle}
                            </div>
                          </div>
                        )}

                        {meta?.department && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Department *
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.department === "Other"
                                ? meta.customDepartment
                                : meta.department}
                            </div>
                          </div>
                        )}

                        {meta?.employmentType && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Employment Type *
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.employmentType}
                            </div>
                          </div>
                        )}

                        {meta?.experienceLevel && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Experience Level *
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.experienceLevel}
                            </div>
                          </div>
                        )}

                        {meta?.location && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Location *
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.location}
                            </div>
                          </div>
                        )}

                        {meta?.keySkills && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Key Skills
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.keySkills}
                            </div>
                          </div>
                        )}

                        {meta?.salaryRange && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Salary Range
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.salaryRange}
                            </div>
                          </div>
                        )}

                        {meta?.benefits && (
                          <div
                            className={
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }
                          >
                            <label className="block text-sm font-medium text-primary_1 mb-2">
                              Benefits
                            </label>
                            <div
                              className={`p-3 py-2 h-10 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                                !hasResults && isAnalyzing
                                  ? "animate-pulse"
                                  : ""
                              }`}
                            >
                              {meta.benefits}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* {formData.prompt && useDetailedForm && agentCondition && ( */}
                      {agentCondition && (
                        <div>
                          <label
                            className={`block text-sm font-medium text-primary_1 mb-2 ${
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }`}
                          >
                            {agentCondition
                              ? useDetailedForm
                                ? "Additional Requirements"
                                : "Analysis Prompt"
                              : "Analysis Prompt"}
                          </label>
                          <div
                            className={`p-3 bg-bg_sidebar rounded border text-sm text-primary_1 min-h-[60px] ${
                              !hasResults && isAnalyzing ? "animate-pulse" : ""
                            }`}
                          >
                            {/* {chatData?.prompts?.find(
                              (p: any) => p?.prompt_message_id
                            )?.prompt ||
                              "No prompt provided - click edit to add one"} */}
                            {meta?.prompt ||
                              chatData?.prompts?.find(
                                (p: any) => p?.prompt_message_id
                              )?.prompt ||
                              ""}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Edit mode for Job Description Creation and Interview
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="companyName"
                              className="text-sm font-medium mb-2 block"
                            >
                              Company Name *
                            </Label>
                            <Input
                              id="companyName"
                              value={formData.companyName}
                              onChange={(e: any) =>
                                updateField("companyName", e.target.value)
                              }
                              placeholder="e.g., ABC Technologies, XYZ Corp."
                              className="text-sm"
                            />
                          </div>
                        )}

                        <div>
                          <Label
                            htmlFor="jobTitle"
                            className="text-sm font-medium mb-2 block"
                          >
                            Job Title *
                          </Label>
                          <Input
                            id="jobTitle"
                            value={formData.jobTitle}
                            onChange={(e: any) =>
                              updateField("jobTitle", e.target.value)
                            }
                            placeholder="e.g., Senior Software Engineer"
                            className="text-sm"
                          />
                        </div>

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="department"
                              className="text-sm font-medium mb-2 block"
                            >
                              Department *
                            </Label>
                            <Select
                              value={formData.department}
                              onValueChange={(value: any) =>
                                updateField("department", value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select department" />
                              </SelectTrigger>
                              <SelectContent className="bg-white z-50">
                                <SelectItem value="Engineering">
                                  Engineering
                                </SelectItem>
                                <SelectItem value="Marketing">
                                  Marketing
                                </SelectItem>
                                <SelectItem value="Sales">Sales</SelectItem>
                                <SelectItem value="HR">HR</SelectItem>
                                <SelectItem value="Finance">Finance</SelectItem>
                                <SelectItem value="Operations">
                                  Operations
                                </SelectItem>
                                <SelectItem value="Product">Product</SelectItem>
                                <SelectItem value="Design">Design</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            {formData.department === "Other" && (
                              <Input
                                className="mt-2"
                                value={formData.customDepartment}
                                onChange={(e: any) =>
                                  updateField(
                                    "customDepartment",
                                    e.target.value
                                  )
                                }
                                placeholder="Specify department"
                              />
                            )}
                          </div>
                        )}

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="employmentType"
                              className="text-sm font-medium mb-2 block"
                            >
                              Employment Type *
                            </Label>
                            <Select
                              value={formData.employmentType}
                              onValueChange={(value: any) =>
                                updateField("employmentType", value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select employment type" />
                              </SelectTrigger>
                              <SelectContent className="bg-white z-50">
                                <SelectItem value="Full-time">
                                  Full-time
                                </SelectItem>
                                <SelectItem value="Part-time">
                                  Part-time
                                </SelectItem>
                                <SelectItem value="Contract">
                                  Contract
                                </SelectItem>
                                <SelectItem value="Internship">
                                  Internship
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div>
                          <Label
                            htmlFor="experienceLevel"
                            className="text-sm font-medium mb-2 block"
                          >
                            Experience Level *
                          </Label>
                          <Select
                            value={formData.experienceLevel}
                            onValueChange={(value: any) =>
                              updateField("experienceLevel", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select experience level" />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50">
                              <SelectItem value="Entry Level">
                                Entry Level
                              </SelectItem>
                              <SelectItem value="1-3 years">
                                1-3 years
                              </SelectItem>
                              <SelectItem value="3-5 years">
                                3-5 years
                              </SelectItem>
                              <SelectItem value="5+ years">5+ years</SelectItem>
                              <SelectItem value="7+ Years">7+ Years</SelectItem>
                              <SelectItem value="10+ Years">
                                10+ Years
                              </SelectItem>
                              <SelectItem value="12+ Years">
                                12+ Years
                              </SelectItem>
                              <SelectItem value="15+ Years">
                                15+ Years
                              </SelectItem>
                              <SelectItem value="Senior">Senior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="location"
                              className="text-sm font-medium mb-2 block"
                            >
                              Location *
                            </Label>
                            <Input
                              id="location"
                              value={formData.location}
                              onChange={(e: any) =>
                                updateField("location", e.target.value)
                              }
                              placeholder="e.g., New York, Remote, London, Hybrid"
                              className="text-sm"
                            />
                          </div>
                        )}

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="keySkills"
                              className="text-sm font-medium mb-2 block"
                            >
                              Key Skills
                            </Label>
                            <Input
                              id="keySkills"
                              value={formData.keySkills}
                              onChange={(e: any) =>
                                updateField("keySkills", e.target.value)
                              }
                              placeholder="e.g., React, Node.js, System Design"
                              className="text-sm"
                            />
                          </div>
                        )}

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="salaryRange"
                              className="text-sm font-medium mb-2 block"
                            >
                              Salary Range
                            </Label>
                            <Input
                              id="salaryRange"
                              value={formData.salaryRange}
                              onChange={(e: any) =>
                                updateField("salaryRange", e.target.value)
                              }
                              placeholder="e.g., $70,000 - $90,000"
                              className="text-sm"
                            />
                          </div>
                        )}

                        {isJobDescription && (
                          <div>
                            <Label
                              htmlFor="benefits"
                              className="text-sm font-medium mb-2 block"
                            >
                              Benefits
                            </Label>
                            <Input
                              id="benefits"
                              value={formData.benefits}
                              onChange={(e: any) =>
                                updateField("benefits", e.target.value)
                              }
                              placeholder="e.g., Health Insurance, Stock Options"
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="additionalPrompt"
                          className="text-sm font-medium mb-2 block"
                        >
                          Additional Requirements
                        </Label>
                        <Textarea
                          id="additionalPrompt"
                          value={formData.prompt}
                          onChange={(e: any) =>
                            updateField("prompt", e.target.value)
                          }
                          placeholder="Any specific requirements, company culture details..."
                          rows={4}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {combinedFiles?.length > 0 && (
                <div
                  className={`mt-4 p-3 pt-2 bg-bg_sidebar rounded border text-sm text-primary_1 ${
                    !hasResults && isAnalyzing ? "animate-pulse" : ""
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-primary_1 py-2">
                      Selected Files ({combinedFiles?.length})
                    </h4>
                    {shouldShowButton && (
                      <div className="flex justify-center">
                        <button
                          onClick={() => setShowAll(!showAll)}
                          className="w-[120px] flex items-center justify-center gap-2 px-4 py-2 text-xs text-primary_1 hover:text-gray-800 hover:bg-gem_hover rounded-full transition-colors"
                        >
                          {showAll ? (
                            <>
                              Show less
                              <ChevronUp size={16} />
                            </>
                          ) : (
                            <>
                              Show more
                              {/* ({files.length - 3} more files) */}
                              <ChevronDown size={16} />
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                    {getItemsToShow().map((file: any, idx: number) => {
                      const fileName =
                        typeof file === "string"
                          ? file
                          : file?.name || file?.filename || "Unnamed File";
                      const iconSrc = getFileIcon(fileName);

                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-2 justify-between text-sm text-primary_1 border border-custom-border-default p-2 rounded-md bg-white"
                        >
                          <div className="flex gap-2 items-center">
                            <img src={iconSrc} alt="" className="w-6" />
                            <span className="line-clamp-1 break-all">
                              {fileName}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 w-full max-w-[968px] mx-auto">
        <div className="flex px-4 gap-6 border-b">
          <button
            type="button"
            onClick={() => {
              setActiveResTab?.("result");
              setTimeout(
                () =>
                  finalResultRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  }),
                0
              );
            }}
            className={`relative -mb-px flex items-center gap-2 pb-2
        ${
          activeResTab === "result"
            ? "text-primary_1"
            : "text-gray-600 hover:text-primary_1"
        }
      `}
            aria-current={activeResTab === "result" ? "page" : undefined}
          >
            {/* <Sparkles className="w-[18px] h-[18px]" /> */}
            <img
              src="/icons/response/star.svg"
              alt="Sparkles Icon"
              className="w-[24px] h-[24px]"
            />
            <span className="font-semibold text-[18px] leading-6 tracking-[-0.03em]">
              Result
            </span>
            <span
              className={`absolute -bottom-[1px] left-0 h-[2px] w-full rounded-full
        ${activeResTab === "result" ? "bg-primary_1" : "bg-transparent"}`}
            />
          </button>

          {/* Analysis tab */}
          <button
            type="button"
            onClick={() => {
              setActiveResTab?.("analysis");
              setTimeout(
                () =>
                  analyzingTopRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  }),
                0
              );
            }}
            className={`relative -mb-px flex items-center gap-2 pb-2
        ${
          activeResTab === "analysis"
            ? "text-primary_1"
            : "text-gray-600 hover:text-primary_1"
        }
      `}
            aria-current={activeResTab === "analysis" ? "page" : undefined}
          >
            {/* <GitBranch className="w-[18px] h-[18px]" /> */}
            <img
              src="/icons/response/git-branch.svg"
              alt="git Icon"
              className="w-[24px] h-[24px]"
            />

            <span className="font-semibold text-[18px] leading-6 tracking-[-0.03em]">
              Analysis
            </span>
            <span
              className={`absolute -bottom-[1px] left-0 h-[2px] w-full rounded-full
        ${activeResTab === "analysis" ? "bg-primary_1" : "bg-transparent"}`}
            />
          </button>
        </div>
        {activeResTab === "analysis" && (
          <div className="rounded-lg w-full">
            <div className="p-4 flex justify-between items-center w-full">
              <div className="flex gap-4 text-primary_1 font-semibold text-lg items-center">
                <div className="w-7 h-7 bg-primary_1/10 rounded-lg flex items-center justify-center">
                  <Brain className="w-4 h-4 text-primary_1" />
                </div>
                Generation Results
              </div>
              <button
                className="text-sm text-primary_1 flex gap-4 items-center justify-between hover:bg-custom-background active:bg-gem_hover p-2 px-4 min-w-40 rounded-full"
                onClick={() => {
                  setShowAnalysis(!showAnalysis);
                }}
              >
                {showAnalysis ? "Hide Analysis" : "Show Analysis"}
                {showAnalysis ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>
            {showAnalysis && (
              <div>
                <div className="flex flex-col space-y-1.5 px-4 w-full">
                  <CardTitle className="flex items-center space-x-2">
                    {isAnalyzing ? (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Brain className="w-5 h-5 text-blue-600" />
                    )}
                    <span className="text-lg font-semibold text-primary_1">
                      Multi Agent Analysis Results
                    </span>
                    <div
                      className={
                        isComplete
                          ? "border border-custom-green bg-custom-green_bg text-custom-green text-xs px-2 py-0.5 rounded-full"
                          : "hidden"
                      }
                    >
                      {hasError
                        ? "Error"
                        : agentResults.find(
                            (agentResult) =>
                              agentResult.agentName === "Generated Result"
                          )
                        ? "Complete"
                        : isAnalyzing
                        ? "In Progress"
                        : "Ready"}
                    </div>
                  </CardTitle>
                </div>
                <CardContent>
                  {!hasResults && isAnalyzing && (
                    <div className="flex flex-col w-full max-w-[968px] text-center py-4">
                      <div className="space-y-2 px-0 relative">
                        <div className="absolute h-[calc(100%-32px)] top-4 border-l border-custom-border-default animate-pulse"></div>
                        <div className="absolute h-2 w-2 rounded-full top-0 -left-[3px] bg-custom-border-default animate-pulse"></div>
                        <div className="absolute h-2 w-2 rounded-full bottom-4 -left-[3px] bg-custom-border-default animate-pulse"></div>
                        <div
                          className={`flex items-center gap-3 px-4 py-2 rounded-xl justify-between hover:bg-custom-background cursor-pointer`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-7 h-7 bg-primary_1/10 rounded-lg flex items-center justify-center animate-pulse"></div>
                            <div className="space-y-2">
                              <div className="font-semibold bg-primary_1/20 flex gap-2 items-center h-4 w-20 rounded-sm animate-pulse"></div>
                              <p className="text-xs bg-gray-500/20 h-4 w-40 rounded-sm animate-pulse"></p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="border border-custom-border-default w-full animate-pulse rounded-lg p-4 space-y-6">
                            <div className="space-y-2">
                              <div className="font-semibold bg-primary_1/10 flex gap-2 items-center h-4 w-[30%] rounded-sm animate-pulse mb-4"></div>
                              <p className="text-xs bg-primary_1/10 h-4 w-[70%] rounded-sm animate-pulse"></p>
                              <p className="text-xs bg-primary_1/10 h-4 w-[85%] rounded-sm animate-pulse"></p>
                              <p className="text-xs bg-primary_1/10 h-4 w-[40%] rounded-sm animate-pulse"></p>
                            </div>

                            <div className="space-y-2">
                              <div className="font-semibold bg-primary_1/10 flex gap-2 items-center h-4 w-[25%] rounded-sm animate-pulse mb-4"></div>
                              <p className="text-xs bg-primary_1/10 h-4 w-[100%] rounded-sm animate-pulse"></p>
                              <p className="text-xs bg-primary_1/10 h-4 w-[85%] rounded-sm animate-pulse"></p>
                              <p className="text-xs bg-primary_1/10 h-4 w-[90%] rounded-sm animate-pulse"></p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {hasError && (
                    <div className="text-center py-4">
                      <div className="text-red-600 mb-4">
                        <p className="font-medium">
                          An error occurred during analysis
                        </p>
                        <p className="text-sm mt-2">
                          {combinedEvents.find((e: any) => e.type === "error")
                            ?.data?.message || "Unknown error"}
                        </p>
                      </div>
                    </div>
                  )}

                  {hasResults && (
                    <div className="space-y-2 my-4 px-0 relative">
                      <div className="absolute h-[calc(100%-32px)] top-4 border-l border-custom-border-default"></div>
                      <div className="absolute h-2 w-2 rounded-full top-0 -left-[3px] bg-custom-border-default"></div>
                      <div className="absolute h-2 w-2 rounded-full bottom-4 -left-[3px] bg-custom-border-default"></div>

                      {agentResults
                        .filter(
                          (agent) => agent.agentName !== "Generated Result"
                        )
                        .map((agentResult, index) => (
                          <div
                            ref={(el) => (itemRefs.current[index] = el)}
                            key={index}
                            style={{ scrollMarginTop: "64px" }}
                            className="p-0 rounded-xl"
                          >
                            <div
                              className={`flex items-center gap-3 px-4 py-2 ${
                                expandedAgents[index]
                                  ? "rounded-xl"
                                  : "rounded-xl"
                              } justify-between hover:bg-custom-background cursor-pointer`}
                              onClick={() => toggleAgentVisibility(index)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-7 h-7 bg-primary_1/10 rounded-lg flex items-center justify-center">
                                  <Brain className="w-4 h-4 text-primary_1" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-primary_1 flex gap-2 items-center">
                                    {agentResult.agentName}
                                    {index === agentResults.length - 1 &&
                                    isAnalyzing &&
                                    !isComplete ? (
                                      <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4 text-green-600" />
                                    )}
                                  </h3>

                                  <p className="text-xs text-gray-500">
                                    Completed at{" "}
                                    {moment
                                      .tz(
                                        agentResult.timestamp,
                                        "America/New_York"
                                      )
                                      .local()
                                      .format("h:mm:ss A")}
                                  </p>
                                </div>
                              </div>

                              <button className="h-8">
                                {expandedAgents[index] ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </button>
                            </div>

                            {expandedAgents[index] && (
                              <div className="agent-response-content p-4">
                                <div className="bg-white p-4 rounded-lg border border-custom-border-default text-primary_1 leading-relaxed text-content">
                                  {/* <ReactMarkdown
                                    components={markdownComponents}
                                  >
                                    {agentResult.result}
                                  </ReactMarkdown> */}
                                  <LlmResponseFormat
                                    generatedText={agentResult.result || ""}
                                    llm={""}
                                    okrs={""}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}

                  {isComplete && !hasResults && !hasError && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-4" />
                      <p className="text-gray-600">
                        Analysis completed, but no detailed results were
                        captured. Please check the console for raw event data.
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </div>
        )}
        {activeResTab === "result" && (
          <div
            className="py-4"
            ref={finalResultRef}
            style={{ scrollMarginTop: "64px" }}
          >
            {agentResults
              .filter(
                (agentResult) => agentResult.agentName === "Generated Result"
              )
              .map((agentResult, index) => {
                return (
                  <div key={index} className={`mt-3 rounded-xl px-4`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className={`w-8 h-8 ${iconKey} rounded flex items-center justify-center`}
                      >
                        <img
                          src={`/icons/response/${iconKey}.svg`}
                          alt=""
                          className="w-4"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-primary_1">
                          {agentResult.agentName}
                        </h3>
                        <p className="text-xs text-gray-500 ">
                          Completed at{" "}
                          {moment
                            .tz(agentResult.timestamp, "America/New_York")
                            .local()
                            .format("h:mm:ss A")}
                        </p>
                        {/* <ReactMarkdown components={markdownComponents}>
                          {agentResult.result}
                        </ReactMarkdown> */}
                      </div>
                    </div>
                    <div className="text-content">
                      <LlmResponseFormat
                        generatedText={agentResult.result || ""}
                        llm={""}
                        okrs={""}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </>
  );
};

export default AgentChatView;
